import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import { API_BASE_URL } from '../utils/api';

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  postcode?: string;
  client_type?: string;
  created_at?: string;
}

const ClientManagement: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [filteredClients, setFilteredClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | undefined>();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    postcode: '',
    client_type: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    const filtered = clients.filter(client =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [clients, searchTerm]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/clients`);
      const clientData = response.data;
      if (Array.isArray(clientData)) {
        setClients(clientData);
        setFilteredClients(clientData);
      } else {
        console.warn('Clients API returned non-array data:', clientData);
        setClients([]);
        setFilteredClients([]);
      }
    } catch (err: any) {
      console.error('Failed to fetch clients:', err);
      setError('Failed to fetch clients');
      setClients([]);
      setFilteredClients([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API_BASE_URL}/api/clients/${editingClient.id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/api/clients`, formData);
      }
      setIsModalOpen(false);
      setEditingClient(undefined);
      resetForm();
      fetchClients();
    } catch (err: any) {
      console.error('Client operation error:', err);
      const errorMessage = err.response?.data?.error || (editingClient ? 'Failed to update client' : 'Failed to create client');
      setError(errorMessage);
    }
  };

  const handleDelete = async (clientId: number) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/clients/${clientId}`);
        fetchClients();
      } catch (err: any) {
        setError('Failed to delete client');
      }
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      address_line1: client.address_line1 || '',
      address_line2: client.address_line2 || '',
      postcode: client.postcode || '',
      client_type: client.client_type || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address_line1: '',
      address_line2: '',
      postcode: '',
      client_type: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h1 className="page-title">Client Management</h1>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                width: '250px'
              }}
            />
            <button 
              onClick={() => setIsModalOpen(true)} 
              className="btn btn-primary"
            >
              Add New Client
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e8f4f8' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Address</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((client) => (
                <tr key={client.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '1rem' }}>{client.name}</td>
                  <td style={{ padding: '1rem' }}>{client.client_type || 'N/A'}</td>
                  <td style={{ padding: '1rem' }}>{client.email || 'N/A'}</td>
                  <td style={{ padding: '1rem' }}>{client.phone || 'N/A'}</td>
                  <td style={{ padding: '1rem' }}>
                    {client.address_line1 ? (
                      <div>
                        {client.address_line1}
                        {client.address_line2 && <div>{client.address_line2}</div>}
                        {client.postcode && <div>{client.postcode}</div>}
                      </div>
                    ) : 'N/A'}
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button 
                        onClick={() => handleEdit(client)} 
                        className="btn btn-primary"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(client.id)} 
                        className="btn btn-danger"
                        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredClients.length === 0 && clients.length > 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              No clients found matching "{searchTerm}".
            </div>
          )}

          {clients.length === 0 && (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              No clients found. Add your first client to get started.
            </div>
          )}
        </div>

        {isModalOpen && (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">{editingClient ? 'Edit Client' : 'Add New Client'}</h2>
                <button onClick={() => {
                  setIsModalOpen(false);
                  setEditingClient(undefined);
                  resetForm();
                }} className="close-btn">&times;</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Type of Client *</label>
                  <select
                    name="client_type"
                    value={formData.client_type}
                    onChange={handleChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select client type...</option>
                    <option value="Letting Agent">Letting Agent</option>
                    <option value="Private">Private</option>
                    <option value="Landlord">Landlord</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Client Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Address Line 1</label>
                  <input
                    type="text"
                    name="address_line1"
                    value={formData.address_line1}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Address Line 2</label>
                  <input
                    type="text"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Postcode</label>
                  <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => {
                    setIsModalOpen(false);
                    setEditingClient(undefined);
                    resetForm();
                  }} className="btn">
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingClient ? 'Update' : 'Create'} Client
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientManagement;